import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Apple-like Badge / Pill
 *
 * Backwards-compatible variants:
 *  - default       (solid primary)
 *  - secondary     (soft neutral)
 *  - destructive   (solid red)
 *  - outline       (outlined neutral)
 *
 * New variants:
 *  - soft          (tonal background + subtle ring)
 *  - glass         (frosted, for translucent toolbars/cards)
 *  - elevated      (solid white chip with a soft shadow)
 *
 * Extras:
 *  - tone: neutral | info | success | warning | danger
 *  - size: xs | sm | md
 *  - shape: md | xl | 2xl | pill
 *  - dot: boolean  (leading status dot)
 */

const badgeVariants = cva(
  [
    "inline-flex items-center justify-center w-fit shrink-0",
    "font-medium whitespace-nowrap select-none",
    "transition-colors duration-200",
    "outline-none focus-visible:outline-none",
    "focus-visible:ring-2 focus-visible:ring-black/10 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    "dark:focus-visible:ring-white/20 dark:focus-visible:ring-offset-neutral-900",
    // icon sizing inside
    "[&>svg]:pointer-events-none [&>svg:not([class*='size-'])]:size-3 [&>svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Solid primary (keeps your legacy "default" feel)
        default:
          "bg-primary text-primary-foreground border border-transparent shadow-sm hover:brightness-95",

        // Legacy secondary -> soft neutral by default
        secondary:
          "border border-transparent bg-gray-100 text-gray-900 ring-1 ring-black/5 hover:bg-gray-200",

        // Solid destructive
        destructive:
          "border border-transparent bg-destructive text-white shadow-sm hover:brightness-95",

        // Neutral outline (tone can override)
        outline:
          "bg-transparent text-gray-700 border border-gray-300 hover:bg-gray-50",

        // New: tonal soft chip (uses `tone`)
        soft: "border border-transparent ring-1",

        // New: glassy chip
        glass:
          "bg-white/60 backdrop-blur-xl text-gray-900 ring-1 ring-black/10 border border-transparent hover:bg-white/70",

        // New: elevated white chip
        elevated:
          "bg-white text-gray-900 ring-1 ring-black/5 border border-transparent shadow-sm hover:shadow-md",
      },
      tone: {
        neutral: "",
        info: "",
        success: "",
        warning: "",
        danger: "",
      },
      size: {
        xs: "h-5 px-2 text-[11px] gap-1",
        sm: "h-6 px-2.5 text-xs gap-1.5",
        md: "h-7 px-3 text-[13px] gap-1.5",
      },
      shape: {
        md: "rounded-md",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
        pill: "rounded-full",
      },
    },
    compoundVariants: [
      // soft + tones
      {
        variant: "soft",
        tone: "neutral",
        className: "bg-gray-100 text-gray-900 ring-black/5 hover:bg-gray-200",
      },
      {
        variant: "soft",
        tone: "info",
        className: "bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100",
      },
      {
        variant: "soft",
        tone: "success",
        className:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100",
      },
      {
        variant: "soft",
        tone: "warning",
        className:
          "bg-amber-50 text-amber-800 ring-amber-200 hover:bg-amber-100",
      },
      {
        variant: "soft",
        tone: "danger",
        className: "bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100",
      },

      // outline + tones
      {
        variant: "outline",
        tone: "neutral",
        className: "text-gray-700 border-gray-300 hover:bg-gray-50",
      },
      {
        variant: "outline",
        tone: "info",
        className: "text-blue-700 border-blue-300 hover:bg-blue-50",
      },
      {
        variant: "outline",
        tone: "success",
        className: "text-emerald-700 border-emerald-300 hover:bg-emerald-50",
      },
      {
        variant: "outline",
        tone: "warning",
        className: "text-amber-800 border-amber-300 hover:bg-amber-50",
      },
      {
        variant: "outline",
        tone: "danger",
        className: "text-rose-700 border-rose-300 hover:bg-rose-50",
      },

      // default solid can optionally respect tone if you pass it
      {
        variant: "default",
        tone: "info",
        className:
          "bg-blue-600 text-white hover:bg-blue-600/95 dark:bg-blue-500",
      },
      {
        variant: "default",
        tone: "success",
        className:
          "bg-emerald-600 text-white hover:bg-emerald-600/95 dark:bg-emerald-500",
      },
      {
        variant: "default",
        tone: "warning",
        className:
          "bg-amber-600 text-white hover:bg-amber-600/95 dark:bg-amber-500",
      },
      {
        variant: "default",
        tone: "danger",
        className:
          "bg-rose-600 text-white hover:bg-rose-600/95 dark:bg-rose-500",
      },
    ],
    defaultVariants: {
      variant: "soft",
      tone: "neutral",
      size: "sm",
      shape: "pill",
    },
  }
);

type BadgeBaseProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    /** Show a leading status dot whose color follows `tone` */
    dot?: boolean;
  };

function dotClassFor(tone: NonNullable<BadgeBaseProps["tone"]>) {
  switch (tone) {
    case "info":
      return "bg-blue-500";
    case "success":
      return "bg-emerald-500";
    case "warning":
      return "bg-amber-500";
    case "danger":
      return "bg-rose-500";
    default:
      return "bg-gray-400";
  }
}
function dotSizeFor(size: NonNullable<BadgeBaseProps["size"]>) {
  switch (size) {
    case "xs":
      return "size-1.5";
    case "md":
      return "size-2";
    default:
      return "size-1.5"; // sm
  }
}

function Badge({
  className,
  variant,
  tone,
  size,
  shape,
  asChild = false,
  dot = false,
  children,
  ...props
}: BadgeBaseProps) {
  const Comp = asChild ? Slot : "span";
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, tone, size, shape }), className)}
      {...props}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={cn(
            "inline-block rounded-full",
            dotSizeFor(size ?? "sm"),
            dotClassFor(tone ?? "neutral"),
            // keep spacing subtle
            "mr-1"
          )}
        />
      )}
      {children}
    </Comp>
  );
}

export { Badge, badgeVariants };
